import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import type {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '../display-image';
import {borderRadius, colors, fontSize, fontWeight, shadows} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

interface MvpFeedbackStep {
    label: string;
    title: string;
    status: 'done' | 'active' | 'upcoming';
}

interface MvpInfoCard {
    title: string;
    body: string;
}

interface MvpFeedbackCtaProps {
    heading: string;
    subheading: string;
    buttonText: string;
    imageSrc: string | StaticImageData;
    onCtaClick?: () => void;
    // The image carries one of two overlays: the stepped progress card, or a pair of
    // small info cards. Whichever set of props is supplied decides which one renders.
    progressTitle?: string;
    progressCaption?: string;
    steps?: MvpFeedbackStep[];
    infoCards?: MvpInfoCard[];
}

const cardSx = {
    bgcolor: colors.background.default,
    borderRadius: borderRadius.xl,
    border: `1px solid ${colors.accent.blueBorder}`,
    boxShadow: shadows.card,
    overflow: 'hidden'
} as const;

// Heading uses the canonical section-heading scale (`sectionHeadingSx`), which carries
// the shared step-down on laptop-class screens (≤1499px) — same as every section that
// renders through `SectionHeader`. Only colour and spacing are section-specific.
const headingSx = [
    sectionHeadingSx,
    {
        color: colors.text.primary,
        mb: 1.5
    }
] as const;

const subheadingSx = {color: colors.text.primary, lineHeight: 1.7, mb: 3} as const;

const textColSx = {p: {xs: 3, md: 5}} as const;

// The overlay card sits on top of the image, so the phone height has to clear it —
// at 280 the taller of the two overlays reached almost to the image's top edge.
const imageColSx = {
    position: 'relative',
    height: {xs: 340, sm: 380, md: '100%'},
    minHeight: {md: 340}
} as const;

const progressCardSx = {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 16,
    bgcolor: colors.background.default,
    borderRadius: borderRadius.lg,
    p: 2.5
} as const;

const progressHeaderSx = {display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5} as const;

const progressTitleSx = {color: colors.text.primary, fontWeight: fontWeight.bold, fontSize: fontSize[14]} as const;
const progressCaptionSx = {color: colors.text.muted, fontSize: fontSize[12]} as const;

const progressBarTrackSx = {
    width: '100%',
    height: 6,
    borderRadius: borderRadius.full,
    bgcolor: colors.background.surfaceAlt,
    overflow: 'hidden',
    mb: 2
} as const;

// The fill sweeps back and forth on its own, so the card reads as validation in
// progress rather than a frozen 55%. `scaleX` keeps it on the compositor, and the
// animation is dropped for anyone who asked for reduced motion.
const progressBarFillSx = {
    width: '100%',
    height: '100%',
    borderRadius: borderRadius.full,
    bgcolor: colors.primary.deep,
    transformOrigin: 'left center',
    transform: 'scaleX(0.55)',
    '@keyframes mvpProgressSweep': {
        '0%': {transform: 'scaleX(0.2)'},
        '100%': {transform: 'scaleX(0.92)'}
    },
    animation: 'mvpProgressSweep 3.2s ease-in-out infinite alternate',
    '@media (prefers-reduced-motion: reduce)': {animation: 'none'}
} as const;

const stepsRowSx = {display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1} as const;

const stepNumberSx = (status: MvpFeedbackStep['status']) =>
    ({
        fontWeight: fontWeight.bold,
        fontSize: fontSize[12],
        color: status === 'upcoming' ? colors.text.muted : colors.primary.deep
    } as const);

const stepLabelSx = (status: MvpFeedbackStep['status']) =>
    ({
        fontSize: fontSize[12],
        color: status === 'upcoming' ? colors.text.muted : colors.text.primary
    } as const);

const infoCardsRowSx = {position: 'absolute', left: 16, right: 16, bottom: 16, display: 'flex', flexWrap: 'wrap', gap: 1.5} as const;

// Grows to share the row, but wraps to its own line rather than squeezing below a
// readable width on the narrowest phones.
const infoCardSx = {flex: '1 1 140px', bgcolor: colors.background.default, borderRadius: borderRadius.lg, p: 2} as const;

const infoCardTitleSx = {color: colors.primary.deep, fontWeight: fontWeight.bold, fontSize: fontSize[14], mb: 0.75} as const;

const infoCardBodySx = {color: colors.text.primary, fontSize: fontSize[12], lineHeight: 1.5} as const;

const MvpFeedbackCta = ({
    heading,
    subheading,
    buttonText,
    imageSrc,
    progressTitle,
    progressCaption,
    steps,
    infoCards,
    onCtaClick
}: MvpFeedbackCtaProps) => {
    return (
        <MUIContainer>
            <Box sx={cardSx}>
                <Grid container>
                    <Grid item xs={12} md={6}>
                        <Box sx={textColSx}>
                            <Typography variant="h1" component="p" className="font-headers" sx={headingSx}>
                                {heading}
                            </Typography>
                            <Typography variant="subtitle1" component="p" className="font-regular-text" sx={subheadingSx}>
                                {subheading}
                            </Typography>
                            <Button
                                variant="contained"
                                disableRipple={true}
                                className="animated-button button-bg-primary"
                                onClick={onCtaClick}
                                endIcon={<ArrowOutward fontSize="small" />}
                            >
                                {buttonText}
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={imageColSx}>
                            <DisplayImage
                                src={imageSrc}
                                alt={heading}
                                fill
                                sizes="(max-width: 900px) 100vw, 50vw"
                                style={{objectFit: 'cover'}}
                            />

                            {infoCards ? (
                                <Box sx={infoCardsRowSx}>
                                    {infoCards.map((card) => (
                                        <Box key={card.title} sx={infoCardSx}>
                                            <Typography component="p" className="font-headers" sx={infoCardTitleSx}>
                                                {card.title}
                                            </Typography>
                                            <Typography component="p" className="font-regular-text" sx={infoCardBodySx}>
                                                {card.body}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                steps && (
                                    <Box sx={progressCardSx}>
                                        {(progressTitle || progressCaption) && (
                                            <Box sx={progressHeaderSx}>
                                                <Typography className="font-headers" sx={progressTitleSx}>
                                                    {progressTitle}
                                                </Typography>
                                                <Typography className="font-regular-text" sx={progressCaptionSx}>
                                                    {progressCaption}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={progressBarTrackSx}>
                                            <Box sx={progressBarFillSx} />
                                        </Box>
                                        <Box sx={stepsRowSx}>
                                            {steps.map((step, idx) => (
                                                <Box key={step.label}>
                                                    <Typography className="font-headers" sx={stepNumberSx(step.status)}>
                                                        {String(idx + 1).padStart(2, '0')}
                                                    </Typography>
                                                    <Typography className="font-headers" sx={stepLabelSx(step.status)}>
                                                        {step.title}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </MUIContainer>
    );
};

export default memo(MvpFeedbackCta);
